import React, { useMemo } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import {
  Box,
  Typography,
  Button,
  Card,
  // Slider,
  Grid,
  CircularProgress,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  Circle,
  GoogleMap,
  InfoWindow,
  Marker,
  useLoadScript,
} from "@react-google-maps/api";
import MappedStores from "./MappedStores";
import ExtractDataFromExcel from "../../../../shared/utils/ExtractDataFromExcel";
import getAddressLatLong from "../../../../shared/utils/getAddressLatLong";
import Slider from "../../../../shared/utils/CustomRangeSlider";
import GlobalButton from "../../../../shared/utils/GlobalButton";

// /** Safely extract { lat, lng } — handles { lat,lng } and { latitude,longitude } */
const toLatLng = (coords) => {
  if (!coords) return null;
  const lat = parseFloat(coords.lat ?? coords.latitude);
  const lng = parseFloat(coords.lng ?? coords.longitude);
  if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) return null;
  return { lat, lng };
};

// // ─── Geocode all store locations with a concurrency cap ───────────────────────
// // Fires getAddressLatLong for every location that doesn't already have coords.
// // CONCURRENCY = 5 means at most 5 Google API calls in-flight at once,
// // which avoids hitting the 50 req/s rate limit for large lists.
// // Returns a new array with locationCoordinates populated on each item.
const CONCURRENCY = 5;

const geocodeLocations = async (locations, onProgress) => {
  const results = [...locations]; // shallow copy — we'll mutate locationCoordinates

  // Split into chunks of CONCURRENCY and process sequentially
  for (let i = 0; i < results.length; i += CONCURRENCY) {
    const chunk = results.slice(i, i + CONCURRENCY);

    await Promise.all(
      chunk.map(async (loc, offsetIdx) => {
        const idx = i + offsetIdx;

        // Skip if already has valid coords (e.g. re-run after partial failure)
        if (toLatLng(results[idx].locationCoordinates)) return;

        const city = loc.details?.city || "";
        const state = loc.details?.state || "";
        const address = loc.details?.address || "";
        const zipCode = loc.details?.zipCode || "";

        try {
          // getAddressLatLong accepts (address, city, state, zipCode)
          // Pass whatever we have; the function handles missing fields gracefully
          const coords = await getAddressLatLong(address, city, state, zipCode);
          results[idx] = {
            ...results[idx],
            locationCoordinates: coords ?? null,
          };
        } catch {
          results[idx] = { ...results[idx], locationCoordinates: null };
        }
      }),
    );

    // Report progress after each chunk so the UI bar moves incrementally
    onProgress(Math.min(i + CONCURRENCY, results.length), results.length);
  }

  return results;
};

// ─── Main component ───────────────────────────────────────────────────────────
const AssignDeliveryCenterInEnquiry = () => {
  const [selectedRFP, setSelectedRFP] = React.useState({});
  const [distance, setDistance] = React.useState(10);
  const [locationsUploadedInEnquiry, setLocationsUploadedInEnquiry] =
    React.useState([]);
  const [batchesCreatedForDeliveryCenter, setBatchesCreatedForDeliveryCenter] =
    React.useState([]);
  const [jobsNotLinkedinBatches, setJobsLinkedInBatches] = React.useState([]);
  const [deliveryCentersNeeded, setDeliveryCentersNeeded] = React.useState([]);
  const [statesInEnquiry, setStatesInEnquiry] = React.useState([]);
  const [selectedMarker, setSelectedMarker] = React.useState(null);
  const [selectedDCId, setSelectedDCId] = React.useState(null);
  const [loading, setLoading] = React.useState(true);

  // ── Geocoding progress: null = not started, 0-100 = in progress, done = null
  const [geocodeProgress, setGeocodeProgress] = React.useState(null); // 0–100 | null

  const { id } = useParams();
  const serverURL = process.env.REACT_APP_URL;
  const token = localStorage.getItem("token");

  // ── Fetch RFP → extract locations → geocode all stores ───────────────────
  const handleSelectedRFP = async () => {
    try {
      const response = await axios.get(
        `${serverURL}/requestForProposal/viewRfp/${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      if (response.data.success !== true) return;

      const { locationsUploaded, locationsUploadedUpdatedAt, ...filteredData } =
        response.data.data;
      setSelectedRFP(filteredData);

      if (!locationsUploaded?.length) return;

      // Step 1 — parse the Excel file
      const { locations } = await ExtractDataFromExcel(locationsUploaded);
      if (!locations.length) return;

      // Derive states for the DC fetch (can start immediately with raw locations)
      const states = [
        ...new Set(locations.map((l) => l.details.state).filter(Boolean)),
      ];
      setStatesInEnquiry(states);
      // Show raw locations in the table immediately — map pins come after geocoding
      setLocationsUploadedInEnquiry(locations);

      // Step 2 — geocode every location in the background
      // The progress bar gives the user feedback during what can be a 10–30 s wait
      setGeocodeProgress(0);
      const geolocated = await geocodeLocations(locations, (done, total) => {
        setGeocodeProgress(Math.round((done / total) * 100));
      });
      setGeocodeProgress(null); // hide bar when done

      // Replace with the enriched version (has locationCoordinates on each item)
      setLocationsUploadedInEnquiry(geolocated);
    } catch (error) {
      console.error(error);
      setGeocodeProgress(null);
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch DCs for the states present in this enquiry ─────────────────────
  const fetchDeliveryCentersInUploadedStates = async () => {
    try {
      const response = await axios.get(
        `${serverURL}/deliveryCenter/viewAll?page=1&limit=500&states=${statesInEnquiry}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response?.data?.success === true) {
        setDeliveryCentersNeeded(response.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  // ── Restore saved vendor batches from DB onto the frontend batch state ─────
  // Called after batchesCreatedForDeliveryCenter is populated (either from
  // handleCreateBatches or on page load).
  //
  // The DB stores raw store objects in storesAllocated[].  We can't restore
  // object references (new Excel parse = new refs), so we match stores by the
  // composite key (storeName + city) and rebuild vendorBatches using the live
  // references from the freshly-geocoded locationsUploadedInEnquiry array.
  // This means drag-drop, reference-equality dedup, and map pins all keep working.
  const restoreSavedBatches = React.useCallback(
    async (freshBatches, geolocatedStores) => {
      try {
        const res = await axios.get(
          `${serverURL}/deliveryCenter/${id}/dc-batches`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!res.data?.success || !res.data.data?.length) return freshBatches;

        // ── Key: storeOwnerPhoneNumber (unique per store instance) ────────────
        // storeName+city is NOT unique — the DB data confirms "Reliance Smart"
        // in "New Delhi" appears across VB-1 and VB-2 (different phone numbers).
        // Phone number is always present in every saved storesAllocated entry.
        const storeRefMap = new Map(); // phoneNumber → live store reference
        for (const store of geolocatedStores) {
          const phone = String(store.details?.storeOwnerPhoneNumber ?? "");
          if (phone && !storeRefMap.has(phone)) {
            storeRefMap.set(phone, store);
          }
        }

        // ── Parse saved rows into per-DC buckets ──────────────────────────────
        // res.data.data = [{ deliveryCenterCity, batches: [rowDoc, ...] }]
        //
        // Two kinds of rows:
        //   vendorId = null  → stores belong directly to the DC (dc.stores),
        //                       not to a vendor sub-batch
        //   vendorId = {...} → stores belong to a vendor sub-batch (vendorBatches)
        //
        // Within each DC we group by vendorId so multiple cityLinked rows for the
        // same vendor get merged into a single vendorBatch entry.
        const savedByCity = new Map();
        // Structure: cityName → { dcStores: Set<liveRef>, vendorMap: Map<vId, vbEntry> }

        for (const group of res.data.data) {
          const city = group.deliveryCenterCity;
          if (!savedByCity.has(city)) {
            savedByCity.set(city, { dcStores: [], vendorMap: new Map() });
          }
          const bucket = savedByCity.get(city);

          for (const row of group.batches) {
            const vId =
              row.vendorId?._id?.toString() ?? row.vendorId?.toString() ?? null;

            // Resolve each saved store to its live reference via phone number
            const liveStores = (row.storesAllocated || [])
              .map((s) =>
                storeRefMap.get(String(s.details?.storeOwnerPhoneNumber ?? "")),
              )
              .filter(Boolean); // drop any that weren't found (should not happen)

            if (!vId) {
              // No vendor → restore these back into dc.stores (not vendorBatches)
              for (const ref of liveStores) {
                if (!bucket.dcStores.includes(ref)) bucket.dcStores.push(ref);
              }
            } else {
              // Has vendor → merge into vendorBatch for this vendor
              if (!bucket.vendorMap.has(vId)) {
                bucket.vendorMap.set(vId, {
                  vendor: row.vendorId, // populated object from .populate()
                  stores: [],
                  batchTitle: row.batchTitle,
                });
              }
              const vb = bucket.vendorMap.get(vId);
              for (const ref of liveStores) {
                if (!vb.stores.includes(ref)) vb.stores.push(ref);
              }
            }
          }
        }

        // ── Merge back onto freshBatches ──────────────────────────────────────
        const restored = freshBatches.map((dc) => {
          const bucket = savedByCity.get(dc.cityName);
          if (!bucket) return dc;

          const vendorBatches = [...bucket.vendorMap.values()].filter(
            (vb) => vb.stores.length > 0,
          );

          // If the DC had stores saved without a vendor, restore them directly
          // onto dc.stores (overwrite the city-matched ones from createBatches
          // so the saved order is respected).
          const restoredStores =
            bucket.dcStores.length > 0 ? bucket.dcStores : dc.stores;

          return {
            ...dc,
            stores: restoredStores,
            ...(vendorBatches.length > 0 ? { vendorBatches } : {}),
          };
        });

        return restored;
      } catch (err) {
        console.error("Could not restore saved batches:", err);
        return freshBatches;
      }
    },
    [serverURL, id, token],
  );

  // ── Batch creation ────────────────────────────────────────────────────────
  // Defined BEFORE the effects so the auto-run useEffect below captures the
  // live function reference (const declarations are not hoisted).
  const handleCreateBatches = React.useCallback(async () => {
    const { batches, pendingStores } = createDeliveryCenterBatches(
      deliveryCentersNeeded,
      locationsUploadedInEnquiry,
    );
    const restored = await restoreSavedBatches(
      batches,
      locationsUploadedInEnquiry,
    );
    setBatchesCreatedForDeliveryCenter(restored);
    setJobsLinkedInBatches(pendingStores);
    setSelectedDCId(null);
  }, [deliveryCentersNeeded, locationsUploadedInEnquiry, restoreSavedBatches]);

  // ── Ref flag: tracks whether auto-batch-creation has run this session ─────
  // Using a ref (not state) so flipping it doesn't trigger a re-render,
  // and it won't cause the effect to re-fire with a stale closure.
  const hasAutoCreated = React.useRef(false);

  React.useEffect(() => {
    handleSelectedRFP();
  }, [id]);

  React.useEffect(() => {
    if (statesInEnquiry.length > 0) fetchDeliveryCentersInUploadedStates();
  }, [statesInEnquiry]);

  // Auto-run: create batches + restore saved assignments once BOTH are ready:
  //   • geocodeProgress === null  → geocoding finished (null on init too, so we
  //                                  also guard on locationsUploadedInEnquiry.length)
  //   • deliveryCentersNeeded.length > 0 → DC list fetched
  //   • hasAutoCreated.current === false  → only once per page load
  React.useEffect(() => {
    if (
      !hasAutoCreated.current &&
      geocodeProgress === null &&
      deliveryCentersNeeded.length > 0 &&
      locationsUploadedInEnquiry.length > 0
    ) {
      hasAutoCreated.current = true;
      handleCreateBatches();
    }
  }, [
    geocodeProgress,
    deliveryCentersNeeded,
    locationsUploadedInEnquiry,
    handleCreateBatches,
  ]);

  // ── Move store: unmapped → a DC ───────────────────────────────────────────
  const handleMoveToMapped = React.useCallback((store, dcId) => {
    // Use object reference (===) for removal — NOT storeName or any value key.
    // Excel-extracted stores have no _id, and multiple stores can share the
    // same storeName (e.g. 8 Reliance Smart branches). Reference identity is
    // the only safe way to remove exactly the one dragged object.
    setJobsLinkedInBatches((prev) => prev.filter((s) => s !== store));
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId ? { ...dc, stores: [...dc.stores, store] } : dc,
      ),
    );
  }, []);

  // ── Move store: a DC → unmapped ───────────────────────────────────────────
  const handleMoveToUnmapped = React.useCallback((store, dcId) => {
    // Same principle: use reference identity for both the DC removal and the
    // duplicate guard when adding back to unmapped.
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId
          ? { ...dc, stores: dc.stores.filter((s) => s !== store) }
          : dc,
      ),
    );
    setJobsLinkedInBatches((prev) =>
      prev.includes(store) ? prev : [...prev, store],
    );
  }, []);

  // ── Bulk move: multiple unmapped stores → one DC ──────────────────────────
  const handleBulkMoveToMapped = React.useCallback((stores, dcId) => {
    // stores[] is an array of the exact same references that are in
    // jobsNotLinkedinBatches, so a Set of references works correctly here too.
    const refSet = new Set(stores);
    setJobsLinkedInBatches((prev) => prev.filter((s) => !refSet.has(s)));
    setBatchesCreatedForDeliveryCenter((prev) =>
      prev.map((dc) =>
        dc._id === dcId ? { ...dc, stores: [...dc.stores, ...stores] } : dc,
      ),
    );
  }, []);

  // ── Save batches to backend ──────────────────────────────────────────────
  // Collects the current frontend batch state and POSTs it to the controller.
  // Called by a "Save Batch" button in VendorsForSelectedDeliveryCenter.
  const [isSaving, setIsSaving] = React.useState(false);

  const handleSaveBatches = React.useCallback(async () => {
    setIsSaving(true);
    try {
      // Build the payload: one entry per (DC, vendor) combination.
      // Each DC can have multiple vendorBatches (set by the user in VendorsForSelectedDC).
      const batches = [];
      for (const dc of batchesCreatedForDeliveryCenter) {
        const vendorBatches = dc.vendorBatches || [];
        if (vendorBatches.length > 0) {
          // Multi-vendor mode: the user has split stores across vendors
          vendorBatches.forEach((vb) => {
            batches.push({
              deliveryCenterCity: dc.cityName,
              vendorId: vb.vendor?._id ?? vb.vendor?.id ?? null,
              batchTitle: vb.batchTitle,
              stores: vb.stores,
            });
          });
        } else {
          // Single-batch mode (no vendor assigned yet): save all stores under null vendor
          batches.push({
            deliveryCenterCity: dc.cityName,
            vendorId: null,
            batchTitle: dc.batchTitle,
            stores: dc.stores,
          });
        }
      }

      await axios.post(
        `${serverURL}/deliveryCenter/${id}/dc-batches/save`,
        { batches },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // Surface success — you can replace with a Swal if you have it imported
      alert("Batch configuration saved successfully!");
    } catch (err) {
      console.error("Error saving batches:", err);
      alert("Failed to save batch configuration.");
    } finally {
      setIsSaving(false);
    }
  }, [batchesCreatedForDeliveryCenter, serverURL, id, token]);

  // ── Derived: the DC the user clicked "View on Map" for ───────────────────
  const selectedDC = useMemo(
    () =>
      batchesCreatedForDeliveryCenter.find((dc) => dc._id === selectedDCId) ??
      null,
    [batchesCreatedForDeliveryCenter, selectedDCId],
  );

  // ── Geocoded count for the sites table status line ────────────────────────
  const geocodedCount = useMemo(
    () =>
      locationsUploadedInEnquiry.filter((l) => toLatLng(l.locationCoordinates))
        .length,
    [locationsUploadedInEnquiry],
  );

  if (loading)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <Box sx={{ background: "#F5F7FB", padding: 3 }}>
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            {selectedRFP?.campaignName}
          </Typography>
          <Typography color="text.secondary">
            Batch & Delivery Centre Configuration
          </Typography>
        </Box>
        <div className="w-30">
          <GlobalButton
            variant="primary"
            size="large"
            leftIcon={<AddIcon />}
            onClick={handleCreateBatches}
          >
            {`Create Batch`}
          </GlobalButton>
        </div>
      </Box>

      {/* ── Geocoding progress bar ───────────────────────────────────────────── */}
      {geocodeProgress !== null && (
        <Box mb={2}>
          <Box display="flex" justifyContent="space-between" mb={0.5}>
            <Typography variant="caption" color="text.secondary">
              Locating stores on map… {geocodeProgress}%
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {geocodedCount} / {locationsUploadedInEnquiry.length} resolved
            </Typography>
          </Box>
          <LinearProgress variant="determinate" value={geocodeProgress} />
        </Box>
      )}

      {/* ── Batch Parameters ─────────────────────────────────────────────────── */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography fontWeight={700} mb={3}>
          Batch Parameters
        </Typography>
        <div className="flex items-center gap-4">
          <Slider
            label="Non Chargeable Delivery Radius"
            unit="KM"
            min={0}
            max={200}
            step={20}
            defaultValue={distance}
            onChange={setDistance}
          />
        </div>
      </Card>

      {/* ── Sites table + Map ────────────────────────────────────────────────── */}
      {/* <Box>
        <Grid container spacing={3}>
          // Map 
          
      {/* ── Batches / Unmapped stores ─────────────────────────────────────────── */}
      <Box mt={3}>
        <MappedStores
          mappedStores={batchesCreatedForDeliveryCenter}
          unmappedStores={jobsNotLinkedinBatches}
          selectedDCId={selectedDCId}
          selectedDC={selectedDC}
          onSelectDC={setSelectedDCId}
          onMoveToMapped={handleMoveToMapped}
          onMoveToUnmapped={handleMoveToUnmapped}
          onBulkMoveToMapped={handleBulkMoveToMapped}
          geocodeProgress={geocodeProgress}
          selectedMarker={selectedMarker}
          setSelectedMarker={setSelectedMarker}
          setSelectedDCId={setSelectedDCId}
          distance={distance}
          onSaveBatches={handleSaveBatches}
          isSaving={isSaving}
          // Receives (dcId, updatedBatches[]) — the child owns all diff/merge logic
          // and hands back the complete new vendorBatches array to keep this simple.
          onAssignVendorToBatch={(dcId, updatedBatches) => {
            setBatchesCreatedForDeliveryCenter((prev) =>
              prev.map((dc) =>
                dc._id === dcId ? { ...dc, vendorBatches: updatedBatches } : dc,
              ),
            );
          }}
        />
      </Box>
    </Box>
  );
};

export default AssignDeliveryCenterInEnquiry;

// ─── Column config ────────────────────────────────────────────────────────────
const columns = [
  { field: "srNo", headerName: "S. NO.", width: 20 },
  { field: "storeName", headerName: "STORE NAME", width: 80 },
  { field: "address", headerName: "ADDRESS", width: 250 },
];

// ─── Batch creation algorithm ─────────────────────────────────────────────────
// Locations passed in here already have locationCoordinates populated by
// geocodeLocations(), so stores inside batches inherit those coordinates and
// the map can plot them immediately via toLatLng(store.locationCoordinates).
const createDeliveryCenterBatches = (deliveryCenters, uploadedStores) => {
  const cityToDC = new Map();
  deliveryCenters.forEach((dc) => {
    cityToDC.set(dc.cityName?.trim().toLowerCase(), dc);
    (dc.linkedCities || []).forEach((city) => {
      cityToDC.set(city.cityName?.trim().toLowerCase(), dc);
    });
  });

  const batches = {};
  deliveryCenters.forEach((dc) => {
    batches[dc._id] = {
      ...dc,
      stores: [],
      batchTitle: `Batch-${Object.keys(batches).length + 1}`,
    };
  });

  const pendingStores = [];
  uploadedStores.forEach((store) => {
    const city = store?.details?.city?.trim().toLowerCase();
    const dc = cityToDC.get(city);
    if (dc) batches[dc._id].stores.push(store);
    else pendingStores.push(store);
  });

  return { batches: Object.values(batches), pendingStores };
};
